import * as React from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { endOfDay, format, isSameDay, parseJSON, startOfDay } from "date-fns";
import enZA from "date-fns/locale/en-ZA";
import { Anchor, Box, Button, CheckBox, FileInput, FormField, Grid, Stack, Tab, Tabs, Text, TextInput } from "grommet";
import { clone } from "lodash";
import randomColor from "randomcolor";
import { DateTimePicker } from "../components/DateTimePicker";
import StyledModal from "../components/StyledModal";
import { ColorPicker } from "../components/ThemeBuilder";
import UserRenderer from "../components/UserRenderer";
import { GetOrganization } from "../organizations/IdentityAPI";
import { Organization } from "../organizations/OrganizationStore";
import * as CalendarStore from "./CalendarStore";
import { CalendarViewModel } from "./CalendarStore";
import { useUser } from "../account/AccountStore";
import CreateEventButton from "./CreateEventButton";
import ScrollContainer from "../components/ScrollContainer";
import { CalendarAccessPolicies } from "./CalendarAccessPolicies";

interface CalendarProps {
    calendar?: CalendarViewModel;
    onChange: (calendar: CalendarStore.CalendarViewModel) => void;
    onDone: Function;
    clear: Function;
}

const defaultCalendar = () => clone({ name: "", color: randomColor(), public: false } as CalendarViewModel);

const CalendarDetails = ({ calendar, onChange, onDone, clear }: CalendarProps) => {
    const [_calendar, setCalendar] = React.useState(calendar);

    const [organization, setOrganization] = React.useState({} as Organization);
    const user = useUser();

    React.useEffect(() => {
        setCalendar(calendar);
    }, [calendar]);

    React.useEffect(() => {
        if (_calendar?.organizationID) {
            GetOrganization(_calendar?.organizationID).then((organization) => {
                setOrganization(organization);
            });
        } else {
            setOrganization(user.organization);
        }
    }, [_calendar?.organizationID]);

    const updateCalendar = () => {
        CalendarStore.UpdateCalendar(_calendar).then((response) => {
            clear();
            if (calendar) {
                onChange(calendar);
            }
            onDone();
        });
    };

    const createCalendar = () => {
        CalendarStore.CreateCalendar(_calendar).then((response) => {
            clear();
        });
    };

    // const startCreateEvent = () => {
    //     setShowCreateEventModal(true);
    // };

    const startEditEvent = (event) => {
        setCurrentEvent(event);
        setShowUpdateEventModal(true);
    };

    const onFilesChange = ({ target: { files } }) => {
        if (files.length === 0) return;

        let reader = new FileReader();
        reader.onload = (evt) => {
            if (evt.target && evt.target.result) {
                let result = evt.target.result.toString();
                setCalendar({ ..._calendar, calendarFile: result.substr(result.indexOf("base64,") + 7) } as CalendarViewModel);
            }
        };

        reader.readAsDataURL(files[0]);
    };

    // const [showCreateEventModal, setShowCreateEventModal] = React.useState(false);
    const [showUpdateEventModal, setShowUpdateEventModal] = React.useState(false);

    const [eventName, setEventName] = React.useState("");
    const [startDate, setStartDate] = React.useState("");
    const [endDate, setEndDate] = React.useState("");
    const [allDayEvent, setAllDayEvent] = React.useState(false);
    const [currentEvent, setCurrentEvent] = React.useState({} as CalendarStore.CalendarEventViewModel);

    React.useEffect(() => {
        setEventName(currentEvent.title);
        setStartDate(currentEvent.startDate);
        setEndDate(currentEvent.endDate);
        setAllDayEvent(currentEvent.allDay);
    }, [currentEvent]);

    // const createEvent = () => {
    //     if (_calendar && _calendar.id) {
    //         CalendarStore.CreateEvent({
    //             allDay: allDayEvent,
    //             calendarID: _calendar.id,
    //             startDate: allDayEvent ? startOfDay(parseJSON(startDate)) : startDate,
    //             endDate: allDayEvent ? endOfDay(parseJSON(endDate)) : endDate,
    //             title: eventName,
    //             metadata: {},
    //         } as CalendarStore.CreateEventRequest).then((response) => {
    //             setShowCreateEventModal(false);
    //             setEventName("");
    //             setStartDate("");
    //             setEndDate("");
    //             setAllDayEvent(false);
    //             onChange(_calendar);
    //         });
    //     }
    // };

    const updateEvent = () => {
        if (_calendar && _calendar.id) {
            CalendarStore.UpdateEvent(
                {
                    allDay: allDayEvent,
                    startDate: allDayEvent ? startOfDay(parseJSON(startDate)) : startDate,
                    endDate: allDayEvent ? endOfDay(parseJSON(endDate)) : endDate,
                    title: eventName,
                    metadata: {},
                } as CalendarStore.CreateEventRequest,
                currentEvent.id
            ).then((response) => {
                setShowUpdateEventModal(false);
                setEventName("");
                setStartDate("");
                setEndDate("");
                setAllDayEvent(false);
                onChange(_calendar);
            });
        }
    };

    const deleteEvent = (event) => {
        CalendarStore.DeleteEvent(event.id).then((response) => {
            if (_calendar) {
                onChange(_calendar);
            }
        });
    };

    const [showPastEvents, setShowPastEvents] = React.useState(false);

    let isExternal = _calendar?.id?.startsWith("ms-");

    return (
        <Box>
            {_calendar && (
                <Box width={"large"} data-calendar-id={_calendar.id}>
                    <Text>{organization?.name}</Text>

                    <Tabs>
                        <Tab title="Details">
                            <Box pad={{ vertical: "small" }}>
                                {!isExternal && (
                                    <>
                                        <Grid columns={{ count: 2, size: "auto" }} gap="small">
                                            <TextInput
                                                id="name"
                                                type="text"
                                                value={_calendar.name}
                                                onChange={(e) => setCalendar({ ..._calendar, name: e.target.value } as CalendarViewModel)}
                                            ></TextInput>

                                            <ColorPicker style={{ backgroundColor: _calendar.color || "black", marginBottom: "0.5rem" }}>
                                                <input type="color" value={_calendar.color || "black"} onChange={(e) => setCalendar({ ..._calendar, color: e.target.value } as CalendarViewModel)} />
                                            </ColorPicker>

                                            <CheckBox
                                                id="public"
                                                type="switch"
                                                label="Public"
                                                checked={_calendar.public}
                                                onChange={(e) => setCalendar({ ..._calendar, public: e.target.checked } as CalendarViewModel)}
                                            ></CheckBox>

                                            <CheckBox
                                                id="microsoft"
                                                type="switch"
                                                label="Sync to Microsoft"
                                                disabled={!user.hasPermission("events:externalCalendars.write")}
                                                checked={_calendar.syncToMicrosoft}
                                                onChange={(e) => setCalendar({ ..._calendar, syncToMicrosoft: e.target.checked } as CalendarViewModel)}
                                            ></CheckBox>
                                        </Grid>

                                        {_calendar?.memberships?.length > 0 && (
                                            <Box>
                                                <Text>Members</Text>
                                                <Box pad="small">
                                                    {_calendar?.memberships?.map((m) => (
                                                        <Box key={m.id}>
                                                            <Text>
                                                                <UserRenderer subjectID={m.userID}></UserRenderer>
                                                            </Text>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            </Box>
                                        )}

                                        <Box direction="row" align="center" gap="xsmall" border={{ side: "bottom" }} pad={{ bottom: "small" }}>
                                            <Anchor href={`/ext/ExternalCalendar/GetCalendar?calendarId=${_calendar.id}`}>External Link</Anchor>
                                            <FontAwesomeIcon icon={["fad", "arrow-down-to-line"]}></FontAwesomeIcon>
                                        </Box>
                                    </>
                                )}

                                <Box direction="row" align="center" justify="between" pad={{ top: "small" }}>
                                    <Text>Events</Text>
                                    {!isExternal && <Anchor onClick={() => setShowPastEvents(!showPastEvents)} label={showPastEvents ? "Hide past events" : "Show past events"} color="brand"></Anchor>}
                                </Box>

                                <Box gap="small">
                                    <Box direction="row" align="center" justify="between">
                                        <CreateEventButton calendars={[_calendar]} onChange={onChange}></CreateEventButton>
                                        {/* <Button onClick={() => startCreateEvent()} label="New Event" icon={<FontAwesomeIcon icon={["fad", "plus"]}></FontAwesomeIcon>}></Button> */}
                                    </Box>
                                    <Box background={"background-back"} pad="small" round="small">
                                        <ScrollContainer direction={"-y"} style={{ maxHeight: "400px" }}>
                                            {_calendar?.calendarEvents
                                                ?.filter((e) => showPastEvents || e.endDate > new Date().toISOString())
                                                .map((event) => (
                                                    <Stack key={event.id} anchor="bottom-right">
                                                        <Box pad="xsmall">
                                                            {/* <Text>{event.title}</Text>
                                            {event.allDay && (
                                                <>
                                                    <Text size="small">All Day</Text>
                                                    <Text size="small">Start {format(parseJSON(event.startDate), "yyyy/MM/dd", { locale: enZA })}</Text>
                                                    <Text size="small">Start {format(parseJSON(event.startDate), "yyyy/MM/dd", { locale: enZA })}</Text>
                                                </>
                                            )}
                                            {!event.allDay && (
                                                <>
                                                    <Text size="small">Start {format(parseJSON(event.startDate), "yyyy/MM/dd HH:mm", { locale: enZA })}</Text>
                                                    <Text size="small">End {format(parseJSON(event.endDate), "yyyy/MM/dd HH:mm", { locale: enZA })}</Text>
                                                </>
                                            )}
                                            {event.metadata?.location?.name && <Text size="small">Location {event.metadata?.location?.name}</Text>}
                                            {event.body && (
                                                <>
                                                    <Text size="small">Body</Text>
                                                    {!event.body?.startsWith("<") && (
                                                        <Text size="small" color="background-contrast" as={"pre"}>
                                                            {event.body}
                                                        </Text>
                                                    )}
                                                    {event.body?.startsWith("<") && <Text size="small" color="background-contrast" dangerouslySetInnerHTML={{ __html: event.body || "" }}></Text>}
                                                </>
                                            )} */}

                                                            <Box
                                                                width="medium"
                                                                gap="xxsmall"
                                                                border={[{ size: "medium", color: event.color, side: "left" }]}
                                                                pad="small"
                                                                round="xsmall"
                                                                background={"background"}
                                                                fill
                                                            >
                                                                <Text size="small" weight="bold">
                                                                    {event.title}
                                                                </Text>
                                                                <Text size="small" color="background-contrast" dangerouslySetInnerHTML={{ __html: event.body || "" }}></Text>

                                                                <Box direction="row" gap="xsmall" align="center">
                                                                    <Text size="small" color="background-contrast">
                                                                        {event.allDay &&
                                                                            isSameDay(parseJSON(event.startDate), parseJSON(event.endDate)) &&
                                                                            `All-Day ${format(parseJSON(event.startDate), "yyyy/MM/dd")}`}

                                                                        {!(event.allDay && isSameDay(parseJSON(event.startDate), parseJSON(event.endDate))) &&
                                                                            `${format(parseJSON(event.startDate), "yyyy/MM/dd HH:mm a")} - ${format(parseJSON(event.endDate), "yyyy/MM/dd HH:mm a")}`}
                                                                    </Text>
                                                                </Box>
                                                            </Box>
                                                        </Box>
                                                        {!isExternal && (
                                                            <Box direction="row">
                                                                <Box hoverIndicator="status-ok" onClick={() => startEditEvent(event)} pad="small">
                                                                    <FontAwesomeIcon icon={["fad", "pencil"]}></FontAwesomeIcon>
                                                                </Box>
                                                                <Box hoverIndicator="status-critical" onClick={() => deleteEvent(event)} pad="small">
                                                                    <FontAwesomeIcon icon={["fad", "trash"]}></FontAwesomeIcon>
                                                                </Box>
                                                            </Box>
                                                        )}
                                                    </Stack>
                                                ))}
                                            {_calendar?.calendarEvents?.filter((e) => showPastEvents || e.endDate > new Date().toISOString()).length === 0 && (
                                                <Text>No events. {_calendar?.calendarEvents?.length != 0 && " See past events"}</Text>
                                            )}
                                        </ScrollContainer>
                                    </Box>
                                </Box>

                                {!isExternal && (
                                    <Box pad="small">
                                        <Text>Create Events from Calendar File</Text>
                                        <FileInput onChange={onFilesChange} accept={".ics"} multiple={false} />
                                        {_calendar.calendarFile && <Text>Click update to load events from file.</Text>}
                                    </Box>
                                )}

                                <Box direction="row" gap="small" justify="end">
                                    {/* <Button className="mr-2" onClick={() => clear()} label="Clear"></Button> */}
                                    <Button secondary onClick={() => clear()} label="Close"></Button>
                                    {!_calendar.id && <Button primary onClick={createCalendar} label="Create"></Button>}
                                    {_calendar.id && <Button primary onClick={updateCalendar} label="Update"></Button>}
                                </Box>
                            </Box>
                        </Tab>
                        <Tab title="Access Policies">
                            <Box pad={{ vertical: "small" }}>
                                <CalendarAccessPolicies calendar={_calendar} />
                            </Box>
                        </Tab>
                    </Tabs>

                    {/* <StyledModal isOpen={showCreateEventModal}>
                        <Box pad="small" width="medium">
                            <FormField label="Event Name">
                                <TextInput id="name" type="text" value={eventName} onChange={(e) => setEventName(e.target.value)}></TextInput>
                            </FormField>
                            <Box>
                                <Text>All Day Event</Text>
                                <CheckBox id="allDay" type="switch" checked={allDayEvent} onChange={(e) => setAllDayEvent(e.target.checked)}></CheckBox>
                            </Box>
                            <FormField label="Start Date">
                                <DateTimePicker value={startDate} onChange={setStartDate} hideTime={allDayEvent}></DateTimePicker>
                            </FormField>
                            <FormField label="End Date">
                                <DateTimePicker value={endDate} onChange={setEndDate} hideTime={allDayEvent}></DateTimePicker>
                            </FormField>

                            <Box direction="row" justify="end" gap="xsmall">
                                <Button secondary onClick={() => setShowCreateEventModal(false)} label="Close"></Button>
                                <Button primary onClick={() => createEvent()} disabled={!eventName} label="Create"></Button>
                            </Box>
                        </Box>
                    </StyledModal> */}

                    <StyledModal isOpen={showUpdateEventModal}>
                        <Box pad="small" width="medium">
                            <FormField label="Event Name">
                                <TextInput id="name" type="text" value={eventName} onChange={(e) => setEventName(e.target.value)}></TextInput>
                            </FormField>
                            <Box>
                                <Text>All Day Event</Text>
                                <CheckBox id="allDay" type="switch" checked={allDayEvent} onChange={(e) => setAllDayEvent(e.target.checked)}></CheckBox>
                            </Box>
                            <FormField label="Start Date">
                                <DateTimePicker value={startDate} onChange={setStartDate} hideTime={allDayEvent}></DateTimePicker>
                            </FormField>
                            <FormField label="End Date">
                                <DateTimePicker value={endDate} onChange={setEndDate} hideTime={allDayEvent}></DateTimePicker>
                            </FormField>

                            <Box direction="row" justify="end" gap="xsmall">
                                <Button secondary onClick={() => setShowUpdateEventModal(false)} label="Close"></Button>
                                <Button primary onClick={() => updateEvent()} disabled={!eventName} label="Update"></Button>
                            </Box>
                        </Box>
                    </StyledModal>
                </Box>
            )}
            {!_calendar && <Button primary onClick={() => onChange(defaultCalendar())} label="New Calendar"></Button>}
        </Box>
    );
};

export default CalendarDetails;
